import { type GetShippingWeekQuery, ShippingWeekStatus } from '@codezo/graphql';
import { Check, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { relativeDay } from './actionQueueFormat';

export type ShippingWeekData = GetShippingWeekQuery['getShippingWeek'];
type Entry = ShippingWeekData['days'][number]['entries'][number];

const utc = (opts: Intl.DateTimeFormatOptions) =>
	new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...opts });
const weekday = utc({ weekday: 'short' });
const dayNum = utc({ day: 'numeric' });
const dayMonth = utc({ day: 'numeric', month: 'short' });

function Method({ express }: { express: boolean }) {
	return express ? (
		<span className="inline-flex items-center gap-1">
			<Zap size={10} className="fill-accent text-accent" /> Express
		</span>
	) : (
		<span>Standard</span>
	);
}

function Card({ e }: { e: Entry }) {
	const missed = e.status === ShippingWeekStatus.Missed;
	const shipped = e.status === ShippingWeekStatus.Shipped;
	return (
		<Link
			to={`/orders/${e.orderId}`}
			className={cn(
				'flex flex-col gap-0.5 rounded-lg border px-2.5 py-2 transition-colors',
				missed
					? 'border-status-danger/30 bg-status-danger-soft hover:border-status-danger/50'
					: 'border-line-2 bg-surface hover:border-line-3',
				shipped && 'opacity-70'
			)}
		>
			<span
				className={cn(
					'truncate text-sm font-medium',
					missed ? 'text-text-1' : 'text-text-1'
				)}
			>
				{e.organization?.name ?? e.orderName}
			</span>
			<span
				className="truncate text-xs text-text-3"
				title={`${e.products} · ${e.orderNumber}`}
			>
				{e.products}
				{e.track ? ` · ${e.track}` : ''}
			</span>
			<span
				className={cn(
					'mt-0.5 truncate text-xs',
					missed ? 'text-status-danger' : shipped ? 'text-text-4' : 'text-text-2'
				)}
			>
				{missed && <>Missed · ready since {relativeDay(new Date(e.at))}</>}
				{shipped && (
					<span className="inline-flex items-center gap-1">
						<Check size={10} /> Shipped
					</span>
				)}
				{e.status === ShippingWeekStatus.Ready && (
					<span className="inline-flex items-center gap-1.5">
						Ready · <Method express={e.express} />
					</span>
				)}
				{e.status === ShippingWeekStatus.Planned && (
					<span className="inline-flex items-center gap-1.5">
						Expected · <Method express={e.express} />
					</span>
				)}
			</span>
		</Link>
	);
}

/**
 * Monday to Sunday: what left, what is ready, what was missed and what production expects to finish,
 * a column per day. Days before today are history; today is marked.
 */
export function ShippingWeek({ week, loading }: { week?: ShippingWeekData; loading: boolean }) {
	const first = week?.days[0]?.date;
	const last = week?.days[6]?.date;
	return (
		<section
			id="shipping-week"
			className="overflow-hidden rounded-xl border border-line-1 bg-surface"
			aria-labelledby="week-h"
		>
			<div className="flex h-11 items-center gap-2 border-b border-line-1 px-4">
				<h2
					id="week-h"
					className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
				>
					Shipping this week
				</h2>
				{first && last && (
					<span className="text-sm text-text-4 tabular-nums">
						{dayNum.format(new Date(first))}–
						{dayMonth.format(new Date(last)).replace('Sept', 'Sep')}
					</span>
				)}
				<span className="flex-1" />
				<span className="hidden items-center gap-3.5 text-xs text-text-3 sm:inline-flex">
					<span className="inline-flex items-center gap-1">
						<Zap size={10} className="fill-accent text-accent" /> Express
					</span>
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full border border-text-3" /> Standard
					</span>
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full bg-status-danger" /> Missed
					</span>
				</span>
			</div>
			<div className="overflow-x-auto">
				<div className="grid min-w-[840px] grid-cols-7">
					{(week?.days ?? Array.from({ length: 7 }, () => null)).map((d, i) => {
						const isToday = !!d && !!week && d.date === week.today;
						const past = !!d && !!week && d.date < week.today;
						return (
							<div
								// eslint-disable-next-line react/no-array-index-key
								key={d?.date ?? i}
								className={cn(
									'flex min-h-[150px] flex-col gap-2 border-line-1 p-2.5',
									i > 0 && 'border-l',
									isToday &&
										'bg-surface-hover shadow-[inset_0_2px_0_var(--color-accent)]'
								)}
							>
								<div className="flex items-baseline gap-1.5 px-0.5">
									{d ? (
										<>
											<span
												className={cn(
													'font-mono text-2xs uppercase tracking-[0.08em]',
													isToday ? 'text-text-1' : 'text-text-4'
												)}
											>
												{weekday.format(new Date(d.date))}
											</span>
											<span
												className={cn(
													'text-sm tabular-nums',
													isToday
														? 'font-medium text-text-1'
														: past
															? 'text-text-4'
															: 'text-text-2'
												)}
											>
												{dayNum.format(new Date(d.date))}
											</span>
											{isToday && (
												<span className="ml-auto text-2xs font-medium text-accent">
													Today
												</span>
											)}
										</>
									) : (
										<span className="h-4 w-12 animate-pulse rounded bg-surface-fill" />
									)}
								</div>
								{d && d.entries.length === 0 && !loading && (
									<span className="px-0.5 text-xs text-text-4">No shipments</span>
								)}
								{d?.entries.map((e) => (
									<Card key={e.id} e={e} />
								))}
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
